import React from "react";
import { motion } from "framer-motion";
import { Handshake } from "lucide-react";

type ClientInput =
  | string
  | {
      name?: string;
      title?: string;
      sector?: string;
      location?: string;
      note?: string;
      logo?: string;
      image?: string;
      details?: string[];
    };

type ClientCard = {
  name: string;
  sector?: string;
  location?: string;
  note?: string;
  logo?: string;
  details: string[];
};

type DivisionClientsProps = {
  content?: {
    title?: string;
    desc?: string;
    clients?: ClientInput[];
    clientsText?: string;
    logoUrls?: string;
    logoImages?: Array<string | undefined>;
    divisionLabel?: string;
    footer?: string;
    footerDesc?: string;
  };
};

const splitClientText = (text?: string) => {
  if (!text) return [];

  return text
    .split(/\r?\n|;/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const normalizeImage = (image?: string) => {
  if (!image) return "";
  if (image.startsWith("http") || image.startsWith("data:") || image.startsWith("blob:")) return image;
  if (image.startsWith("/")) return `https://hamdanoman.com${image}`;
  return image;
};

const normalizeName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "");

const isFilledString = (value?: string): value is string => Boolean(value);

const toClientCard = (client: ClientInput): ClientCard | null => {
  if (typeof client === "string") {
    const parts = client
      .split("|")
      .map((part) => part.trim())
      .filter(Boolean);

    if (!parts[0]) return null;

    return {
      name: parts[0],
      sector: parts[1],
      location: parts[2],
      note: parts[3],
      details: parts.slice(1),
    };
  }

  const name = client.name || client.title;
  if (!name) return null;

  return {
    name,
    sector: client.sector,
    location: client.location,
    note: client.note,
    logo: normalizeImage(client.logo || client.image),
    details: client.details || [client.sector, client.location, client.note].filter(isFilledString),
  };
};

const applyClientLogos = (
  clients: ClientCard[],
  content?: DivisionClientsProps["content"]
) => {
  const logoByIndex: string[] = [];
  const logoByName = new Map<string, string>();

  splitClientText(content?.logoUrls).forEach((line, index) => {
    const parts = line.split("|").map((part) => part.trim()).filter(Boolean);
    const image = normalizeImage(parts.length === 1 ? parts[0] : parts[1]);
    if (!image) return;

    logoByIndex[index] = image;
    if (parts.length > 1) {
      logoByName.set(normalizeName(parts[0]), image);
    }
  });

  (content?.logoImages || []).forEach((image, index) => {
    const normalized = normalizeImage(image);
    if (normalized && !logoByIndex[index]) logoByIndex[index] = normalized;
  });

  return clients.map((client, index) => ({
    ...client,
    logo: client.logo || logoByName.get(normalizeName(client.name)) || logoByIndex[index],
  }));
};

const buildClients = (content?: DivisionClientsProps["content"]) => {
  const fromArray = (content?.clients || []).map(toClientCard).filter(Boolean) as ClientCard[];
  if (fromArray.length > 0) return applyClientLogos(fromArray, content);

  const fromText = splitClientText(content?.clientsText)
    .map(toClientCard)
    .filter(Boolean) as ClientCard[];

  return applyClientLogos(fromText, content);
};

const DivisionClients: React.FC<DivisionClientsProps> = ({ content }) => {
  const clients = buildClients(content);
  const title = content?.title || "Our Clients";
  const desc =
    content?.desc ||
    "Trusted clients across Hamdan Group divisions and operations in Oman.";
  const footer = content?.footer;
  const footerDesc = content?.footerDesc;

  return (
    <section className="bg-white py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="mb-8 text-center"
        >
          <h2 className="text-3xl font-bold text-slate-950 md:text-4xl">
            {title}
          </h2>
          {desc && (
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 md:text-base">
              {desc}
            </p>
          )}
        </motion.div>

        {clients.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-amber-300 bg-[#fffaf0] px-6 py-12 text-center text-slate-600">
            No clients added yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {clients.map((client, index) => {
              return (
                <motion.article
                  key={`${client.name}-${index}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.2) }}
                  className="flex min-h-[150px] flex-col items-center justify-center rounded-[18px] border border-[#f2bb13] bg-white px-5 py-4 shadow-[0_10px_26px_rgba(15,23,42,0.06)]"
                >
                  <div className="flex h-[86px] w-full items-center justify-center">
                    {client.logo ? (
                      <img
                        src={client.logo}
                        alt={`${client.name} logo`}
                        className="max-h-[82px] max-w-[205px] object-contain"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-[16px] bg-gradient-to-br from-[#f5862f] to-[#d94b12] text-white shadow-[0_10px_20px_rgba(217,75,18,0.18)]">
                        <Handshake className="h-9 w-9" />
                      </div>
                    )}
                  </div>

                  <div className="mt-2 min-w-0 max-w-full">
                    <h3 className="break-words text-center text-base font-extrabold uppercase leading-snug text-[#8b1538] md:text-lg">
                      {client.name}
                    </h3>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}

        {(footer || footerDesc) && (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="mx-auto mt-10 max-w-3xl text-center text-sm font-medium text-slate-600 md:text-base"
          >
            {footer && <span className="font-bold text-slate-950">{footer}</span>}
            {footerDesc ? ` ${footerDesc}` : ""}
          </motion.p>
        )}
      </div>
    </section>
  );
};

export default DivisionClients;
